import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Clock, Star, Reply } from 'lucide-react';
import * as planService from '../../../services/planService';

/**
 * Coach Notes Component
 * Display coach feedback and notes with timestamp
 */
export default function CoachNotes({ planId }) {
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (planId) {
      loadPlan();
    }
  }, [planId]);

  const loadPlan = async () => {
    setIsLoading(true);
    try {
      const result = await planService.getPlanDetails(planId);
      if (result.success) {
        setPlan(result.data);
      }
    } catch (error) {
      console.error('Error loading plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading coach notes...</p>
        </div>
      </div>
    );
  }

  const coachNotes = plan?.coach_notes || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Coach Notes</h2>
      </div>

      {/* Main Coach Notes */}
      {coachNotes ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl shadow-lg p-6 border-2 border-primary-200"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-gray-900">Coach Feedback</h3>
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <Clock className="w-4 h-4" />
                <span>
                  Last updated: {plan?.updated_at 
                    ? new Date(plan.updated_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'Recently'}
                </span>
              </div>
              <div className="prose max-w-none">
                <p className="text-gray-800 whitespace-pre-line leading-relaxed">
                  {coachNotes}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No coach notes yet</h3>
          <p className="text-gray-600">
            Your coach hasn't added any notes for this plan yet. Check back soon!
          </p>
        </div>
      )}

      {/* Additional Notes Section (for future expansion) */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Updates</h3>
        <p className="text-gray-600 text-sm">
          Additional coach notes and updates will appear here as your coach adds them.
        </p>
      </div>
    </div>
  );
}
